import { LoaderIcon } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import { useDebouncedValue } from "@/hooks/use-debounced-value";

export interface AsyncSearchComboboxProps<T> {
	fetchUrl: string;
	queryParam?: string;
	extraParams?: Record<string, string>;
	getKey: (item: T) => string;
	renderItem: (item: T) => ReactNode;
	placeholder: string;
	emptyText: string;
	onSelect: (item: T) => void;
}

export function AsyncSearchCombobox<T>({
	fetchUrl,
	queryParam = "q",
	extraParams = {},
	getKey,
	renderItem,
	placeholder,
	emptyText,
	onSelect,
}: AsyncSearchComboboxProps<T>) {
	const [query, setQuery] = useState("");
	const [items, setItems] = useState<T[]>([]);
	const [loading, setLoading] = useState(false);
	const debounced = useDebouncedValue(query, 300);

	// biome-ignore lint/correctness/useExhaustiveDependencies: keyed on serialized extraParams to avoid re-running on new object identity
	useEffect(() => {
		let cancelled = false;
		async function run() {
			if (debounced.trim().length < 2) {
				setItems([]);
				return;
			}
			setLoading(true);
			try {
				const params = new URLSearchParams({
					[queryParam]: debounced,
					...extraParams,
				});
				const res = await fetch(`${fetchUrl}?${params.toString()}`, {
					headers: { Accept: "application/json" },
				});
				const data = (await res.json()) as T[];
				if (!cancelled) setItems(Array.isArray(data) ? data : []);
			} catch {
				if (!cancelled) setItems([]);
			} finally {
				if (!cancelled) setLoading(false);
			}
		}
		run();
		return () => {
			cancelled = true;
		};
	}, [debounced, fetchUrl, queryParam, JSON.stringify(extraParams)]);

	return (
		<Command shouldFilter={false} className="border rounded-md">
			<CommandInput
				value={query}
				onValueChange={setQuery}
				placeholder={placeholder}
			/>
			<CommandList>
				{loading && (
					<div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
						<LoaderIcon className="size-4 animate-spin" /> …
					</div>
				)}
				{!loading && <CommandEmpty>{emptyText}</CommandEmpty>}
				<CommandGroup>
					{items.map((item) => (
						<CommandItem
							key={getKey(item)}
							value={getKey(item)}
							onSelect={() => onSelect(item)}
						>
							{renderItem(item)}
						</CommandItem>
					))}
				</CommandGroup>
			</CommandList>
		</Command>
	);
}
